/**
 * TeamSystem Impact - Compliance Path Dashboard (vista amministratore)
 *
 * Il "respiro" della compliance: scena di brand a sinistra, percorso verticale a
 * destra (Inspira: Engagement → Doppia Materialità → Raccolta; Espira: Reporting),
 * con stato/percentuale per tappa e drill-down al clic.
 */

import { useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
  Forum as ForumIcon,
  Balance as BalanceIcon,
  DataUsage as DataUsageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BrandScene } from './BrandScene';
import { PhaseStep } from './PhaseStep';
import { EngagementDrill } from './EngagementDrill';
import { DmaDrill } from './DmaDrill';
import { CollectionDrill } from './CollectionDrill';
import { useCompliancePath } from './useCompliancePath';

type StepId = 'engagement' | 'dma' | 'collection';

const COLORS = {
  engagement: '#0277BD',
  dma: '#2E7D32',
  collection: '#ED6C02',
  reporting: '#6A1B9A',
};

interface CompliancePathDashboardProps {
  year: number;
}

export const CompliancePathDashboard = ({ year }: CompliancePathDashboardProps) => {
  const { t } = useTranslation();
  const data = useCompliancePath(year);
  const [expanded, setExpanded] = useState<StepId | null>(null);

  const toggle = (id: StepId) => setExpanded((cur) => (cur === id ? null : id));

  const engChips = [
    t('dashboard.path.chipCampaigns', { n: data.engagement.campaignsTotal }),
    t('dashboard.path.chipResponses', { n: data.engagement.responses }),
  ];
  const dmaChips = [
    t('dashboard.path.chipMaterialIros', { n: data.dma.materialIros }),
    t('dashboard.path.chipAreas', { n: data.dma.areas.length }),
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: 'calc(100vh - 112px)',
        borderRadius: 3,
        px: { xs: 2, md: 3 },
        py: 3,
      }}
    >
      {/* Sfondo di brand che "respira", velato di chiaro */}
      <BrandScene />

      {/* Contenuti in primo piano, colonna centrata */}
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 880, mx: 'auto' }}>
        <Box mb={3}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            {t('dashboard.path.heading')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboard.path.subheading', { year })}
          </Typography>
        </Box>

        {data.loading ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={110} />
            ))}
          </Box>
        ) : (
          <>
              <PhaseStep
                icon={<ForumIcon />}
                color={COLORS.engagement}
                title={t('dashboard.path.engagement')}
                description={t('dashboard.path.engagementDesc')}
                state={data.engagement.result.state}
                percent={data.engagement.result.percent}
                chips={engChips}
                expanded={expanded === 'engagement'}
                onToggle={() => toggle('engagement')}
              >
                <EngagementDrill data={data.engagement} />
              </PhaseStep>

              <PhaseStep
                icon={<BalanceIcon />}
                color={COLORS.dma}
                title={t('dashboard.path.dma')}
                description={t('dashboard.path.dmaDesc')}
                state={data.dma.result.state}
                percent={data.dma.result.percent}
                chips={dmaChips}
                expanded={expanded === 'dma'}
                onToggle={() => toggle('dma')}
              >
                <DmaDrill data={data.dma} />
              </PhaseStep>

              <PhaseStep
                icon={<DataUsageIcon />}
                color={COLORS.collection}
                title={t('dashboard.path.collection')}
                description={t('dashboard.path.collectionDesc')}
                state={data.collection.result.state}
                bars={[
                  { label: t('dashboard.path.colCompiled'), percent: data.collection.compiledPct },
                  { label: t('dashboard.path.colValidated'), percent: data.collection.validatedPct },
                ]}
                expanded={expanded === 'collection'}
                onToggle={() => toggle('collection')}
              >
                <CollectionDrill data={data.collection} />
              </PhaseStep>

              <PhaseStep
                icon={<DescriptionIcon />}
                color={COLORS.reporting}
                title={t('dashboard.path.reporting')}
                description={t('dashboard.path.reportingDesc')}
                state={null}
                interactive={false}
                isLast
              />
            </>
          )}
        </Box>
      </Box>
  );
};
